import React from "react";
import {
  CardContent,
  Typography,
  Box,
  Stack,
  Chip,
  Divider,
} from "@mui/material";
import { StyledCard } from "../common/StyledCard";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import CallMadeIcon from "@mui/icons-material/CallMade";
import { formatCurrency } from "../../utils/currencyUtils";
import { PaymentSummaryDto } from "../../shared/dtos/payment/PaymentSummaryDto";

interface PaymentCardProps {
  payment: PaymentSummaryDto;
}

const PaymentCard: React.FC<PaymentCardProps> = ({ payment }) => {
  const isInflow = payment.direction === "INFLOW";

  // Determine related document for display
  const relatedDoc =
    payment.receiptNumber ||
    payment.invoiceNumber ||
    payment.poNumber ||
    payment.creditNoteNumber ||
    payment.expenseCategory ||
    "N/A";

  return (
    <StyledCard
      sx={{
        borderLeft: "4px solid",
        borderLeftColor: isInflow ? "success.main" : "error.main",
      }}
    >
      <CardContent>
        <Stack
          sx={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                color: isInflow ? "success.main" : "error.main",
              }}
            >
              {isInflow ? "+" : "-"}
              {formatCurrency(payment.amount)}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {new Date(payment.createdAt).toLocaleString()}
            </Typography>
          </Box>
          <Chip
            icon={isInflow ? <CallReceivedIcon /> : <CallMadeIcon />}
            label={payment.direction}
            size="small"
            color={isInflow ? "success" : "error"}
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: "0.7rem" }}
          />
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        {/* --- ROW: METHOD --- */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Method
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {payment.paymentMethodName}
          </Typography>
        </Box>

        {/* --- ROW: TYPE --- */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Type
          </Typography>
          <Typography variant="body2">
            {payment.paymentType
              ? payment.paymentType.replace(/_/g, " ")
              : "N/A"}
          </Typography>
        </Box>

        {/* --- ROW: RELATED DOC --- */}
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Related Doc
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, color: "primary.main" }}
          >
            {relatedDoc}
          </Typography>
        </Box>
      </CardContent>
    </StyledCard>
  );
};

export default PaymentCard;
